// The CRM's contact history (#1925): one person's messages on every channel, newest first, from the
// `getCustomerHistory` callable. Reads nothing until an admin asks for someone, by the form or by
// #history?uid=<uid> / #history?email=<address> (the Users card and the inbox's customer panel link here).

import { BADGE, BADGE_DOT, BUTTON, CARD, EMPTY_STATE } from "./ui/classes.js";
import { icon } from "./ui/icons.js";
import { escapeHTML } from "./familyDetailsPanel.js";
import { withBusy } from "./ui/busy.js";

let ctx = null;
let lastKey = null;

/** Each channel's name and icon. The icon's ring colour says who wrote: paprika for them, gray for us. */
const CHANNELS = {
  email: { label: "Email", icon: "envelope" },
  feedback: { label: "In-app feedback", icon: "chat-bubble-left-right" },
  app_store: { label: "App Store review", icon: "star" },
  google_play: { label: "Google Play review", icon: "star" },
  waitlist: { label: "Waitlist", icon: "user-plus" },
  trial_reminder: { label: "Trial reminder", icon: "envelope" }
};
const DELIVERY = {
  sent: ["gray", "Sent"],
  delivered: ["green", "Delivered"],
  delayed: ["yellow", "Delayed"],
  opened: ["green", "Opened"],
  clicked: ["green", "Clicked"],
  bounced: ["red", "Bounced"],
  complained: ["red", "Marked as spam"],
  failed: ["red", "Failed"],
  suppressed: ["red", "Suppressed"]
};
const THREAD_STATUS = { open: "Open", waiting: "Waiting", handled: "Handled", spam: "Spam", snoozed: "Snoozed" };

export async function loadHistoryTemplate() {
  const container = document.getElementById("sectionHistoryContainer");
  if (!container) return;
  try {
    const res = await fetch("templates/history.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load history.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the contact history template: ${escapeHTML(err.message)}</div>`;
  }
}

/** The address for one person's history. */
export function historyHref({ uid, email } = {}) {
  if (uid) return `#history?uid=${encodeURIComponent(uid)}`;
  if (email) return `#history?email=${encodeURIComponent(email)}`;
  return "#history";
}

/** What a #history?… address asks for: { uid } or { email }, or null. */
export function historyQueryFromHash(hash) {
  const [section, queryString] = String(hash || "").replace("#", "").split("?");
  if (section.toLowerCase() !== "history" || !queryString) return null;
  const params = new URLSearchParams(queryString);
  const uid = params.get("uid")?.trim();
  if (uid) return { uid };
  const email = params.get("email")?.trim();
  return email ? { email } : null;
}

/** What the form's text is: an address, or else a user ID. */
export function queryOf(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  return text.includes("@") ? { email: text } : { uid: text };
}

function when(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function dayOf(iso) {
  const date = new Date(iso || "");
  return Number.isNaN(date.getTime()) ? "Undated" : date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function deliveryBadge(delivery) {
  const key = delivery?.last_event || "sent";
  const [tone, label] = DELIVERY[key] || ["gray", key];
  const at = delivery?.events?.[key];
  return `<span class="${BADGE[tone]}" title="${escapeHTML(at ? when(at) : "")}"><span aria-hidden="true" class="${BADGE_DOT[tone]}"></span>${escapeHTML(label)}</span>`;
}

/** Where a row opens: its conversation, its lead, or the feedback list. */
function openLink(row) {
  if (row.thread_id) {
    const status = THREAD_STATUS[row.thread_status] || "";
    return `<a href="#inbox?thread=${escapeHTML(row.thread_id)}" class="${BUTTON.link}">Open the conversation</a>${status ? ` <span class="text-gray-400 dark:text-gray-500">· ${escapeHTML(status)}</span>` : ""}`;
  }
  if (row.lead_id) return `<a href="#leads" class="${BUTTON.link}">Waitlist leads</a>`;
  if (row.feedback_id) return `<a href="#feedback" class="${BUTTON.link}">User feedback</a>`;
  return "";
}

function rowHTML(row) {
  const channel = CHANNELS[row.channel] || { label: row.channel || "Message", icon: "envelope" };
  const fromThem = row.direction === "in";
  const ring = fromThem
    ? "bg-paprika-50 text-paprika-700 ring-paprika-600/20 dark:bg-paprika-500/10 dark:text-paprika-300 dark:ring-paprika-400/30"
    : "bg-gray-50 text-gray-500 ring-gray-500/10 dark:bg-white/5 dark:text-gray-400 dark:ring-white/10";
  const link = openLink(row);
  return `<li class="relative flex gap-x-4 pb-6" data-history-row="${escapeHTML(row.id)}">
      <div class="absolute inset-y-0 left-0 flex w-8 justify-center"><div class="w-px bg-gray-200 dark:bg-white/10"></div></div>
      <div class="relative flex size-8 flex-none items-center justify-center rounded-full ring-1 ${ring}">${icon(channel.icon, "size-4")}</div>
      <div class="min-w-0 flex-auto">
        <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p class="text-sm/6 text-gray-500 dark:text-gray-400"><span class="font-semibold ${fromThem ? "text-paprika-700 dark:text-paprika-300" : "text-gray-900 dark:text-white"}">${fromThem ? "From them" : "From us"}</span> · ${escapeHTML(channel.label)}${row.automated ? " · automated" : ""}</p>
          <time datetime="${escapeHTML(row.at || "")}" class="flex-none text-xs/6 text-gray-500 dark:text-gray-400">${escapeHTML(when(row.at))}</time>
        </div>
        <p class="text-sm font-medium text-gray-900 dark:text-white">${escapeHTML(row.title || "(no subject)")}</p>
        ${row.snippet ? `<p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">${escapeHTML(row.snippet)}</p>` : ""}
        ${row.delivery || link ? `<div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">${row.delivery ? deliveryBadge(row.delivery) : ""}${link ? `<span>${link}</span>` : ""}</div>` : ""}
      </div>
    </li>`;
}

/** The person and their history, grouped by day. Pure, for tests/admin/history.test.js. */
export function historyHTML(result) {
  const { person, rows = [], truncated } = result || {};
  const who = person?.name || person?.emails?.[0] || person?.uid || "Them";
  const meta = [
    ...(person?.emails || []).map((e) => `<span class="break-all">${escapeHTML(e)}</span>`),
    person?.uid ? `<a href="#users?uid=${encodeURIComponent(person.uid)}" class="${BUTTON.link} font-mono text-xs break-all">${escapeHTML(person.uid)}</a>` : `<span>No account</span>`,
    person?.family_id ? `<a href="#families?id=${encodeURIComponent(person.family_id)}" class="${BUTTON.link} font-mono text-xs break-all">Household ${escapeHTML(person.family_id)}</a>` : ""
  ].filter(Boolean);
  const inCount = rows.filter((r) => r.direction === "in").length;
  const header = `<div class="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <div class="min-w-0">
        <h3 class="${CARD.title}">${escapeHTML(who)}</h3>
        <p class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">${meta.join("")}</p>
      </div>
      <p class="text-sm text-gray-500 dark:text-gray-400">${rows.length} message${rows.length === 1 ? "" : "s"} · ${inCount} from them</p>
    </div>`;

  if (!rows.length) {
    return `<div class="${CARD.panel}">${header}<div class="${EMPTY_STATE.box}">
        ${icon("inbox", EMPTY_STATE.icon)}
        <h3 class="${EMPTY_STATE.title}">Nothing exchanged yet</h3>
        <p class="${EMPTY_STATE.text}">No email, feedback, linked review, waitlist entry or email from us for this person.</p>
      </div></div>`;
  }

  const days = [];
  for (const row of rows) {
    const day = dayOf(row.at);
    if (!days.length || days[days.length - 1].day !== day) days.push({ day, rows: [] });
    days[days.length - 1].rows.push(row);
  }
  const body = days.map(({ day, rows: dayRows }) => `<div>
      <h4 class="mb-3 text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">${escapeHTML(day)}</h4>
      <ul role="list">${dayRows.map(rowHTML).join("")}</ul>
    </div>`).join("");
  const more = truncated
    ? `<p class="mt-2 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300">This is the newest part of a longer history: the 300 newest messages from the 50 latest conversations.</p>`
    : "";
  return `<div class="${CARD.panel}">${header}<div class="flex flex-col gap-6 px-4 py-5 sm:px-6">${more}${body}</div></div>`;
}

export function initHistoryController(options) {
  ctx = options;
  const form = document.getElementById("historyForm");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const query = queryOf(document.getElementById("historyQuery")?.value);
    if (!query) {
      ctx.showToast?.("Type an email address or a user ID.", "warning");
      return;
    }
    const href = historyHref(query);
    if (window.location.hash !== href) window.history.pushState(null, "", href);
    showHistory(query);
  });
}

/** On entering the section: the person the address names, if any. */
export function openHistoryFromHash() {
  const query = historyQueryFromHash(window.location.hash);
  if (!query) return;
  const input = document.getElementById("historyQuery");
  if (input) input.value = query.uid || query.email;
  showHistory(query);
}

async function showHistory(query) {
  const result = document.getElementById("historyResult");
  if (!result || !ctx) return;
  const key = JSON.stringify(query);
  lastKey = key;
  result.innerHTML = `<p class="text-sm text-gray-500 dark:text-gray-400">Reading their history…</p>`;
  await withBusy(document.getElementById("historyLookupBtn"), async () => {
    try {
      const res = await ctx.httpsCallable(ctx.functions, "getCustomerHistory")(query);
      if (lastKey !== key) return;
      result.innerHTML = historyHTML(res.data || {});
    } catch (err) {
      if (lastKey !== key) return;
      result.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Couldn't read the history: ${escapeHTML(err.message)}</div>`;
    }
  });
}
